import { InputVM } from './InputVM';
import { action, computed, makeObservable, observable } from 'mobx';
import { RecordValue, SimpleRecord } from '../interfaces/types';
import { getTransformer, makeMetadata, property } from '../helpers/decorators';

export class ListLikeVM extends InputVM {
  constructor(...args: any[]) {
    super(...args);
    makeObservable(this);
    makeMetadata(this);
  }

  /**
   * @hidden
   * @protected
   */
  @observable.shallow
  private _DATA_: SimpleRecord[] = [];

  @property
  @computed
  get data(): SimpleRecord[] {
    return getTransformer(this, 'dataGetter')(this._DATA_);
  }

  set data(data) {
    if (!Array.isArray(data) && typeof data !== 'undefined') {
      console.error(`[VM]: Type of the data for List must be Array:`, this);
      throw new Error(`[VM]: Type of the data for List must be Array`);
    }
    this._DATA_ = getTransformer(this, 'dataSetter')(data);
  }

  /**
   * @hidden
   */
  $identifierField: string = `value`;

  @action
  setData(data: SimpleRecord[]): this {
    this.data = data;
    return this;
  }

  /**
   * 当前VM的loading信息
   */
  @property
  @observable
  loading: boolean = false;

  @action
  setLoading(loading: boolean): this {
    this.loading = !!loading;
    return this;
  }

  /**
   * 为当前VM添加一行数据
   * @example
   * ```ts
   * $vm.insert({id : "", key0: "", key1: "", ...})
   * ```
   */
  @action
  insertNode(node: SimpleRecord, index: number = -1): this {
    this.data.splice(index === -1 ? this.data.length : index, 0, node);
    return this;
  }

  /**
   * 为当前VM删除一行数据
   * @param key - 待删除数据的Key, 或者当前行数据
   */
  @action
  removeNode(key: SimpleRecord | RecordValue): this {
    const node = this.getNode(key);
    const index = this.data.findIndex((d) => d[this.$identifierField] === node[this.$identifierField]);
    if (index > -1) this.data.splice(index, 1);
    return this;
  }

  /**
   * 更新当前VM的一行数据
   * @param node - 更新的完整信息, node 中必须包含id
   */
  @action
  updateNode(node: SimpleRecord): this {
    const index = this.data.findIndex((d) => d[this.$identifierField] === node[this.$identifierField]);
    if (index > -1) {
      this.data[index] = node;
    } else {
      this.insertNode(node);
    }
    return this;
  }

  /**
   * 检测当前VM是否存在某一行数据
   *  注:  只判断`id`是否存在
   * @param key
   */
  hasNode(key: SimpleRecord | RecordValue): boolean {
    const _key = typeof key === 'object' ? key[this.$identifierField] : key;
    const index = this.data.findIndex((d) => d[this.$identifierField] === _key);
    return index > -1;
  }

  /**
   * 获取`id`对应的行数据
   * @param key
   */
  getNode(key: SimpleRecord | RecordValue): SimpleRecord {
    if (key === undefined || key === null) return undefined;
    if (['string', 'number', 'object', 'boolean'].indexOf(typeof key) === -1) return undefined;
    const _key = typeof key === 'object' ? key![this.$identifierField] : key;
    return this.data?.find((d) => d[this.$identifierField] === _key);
  }
}
